import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref } from 'vue'
import { buildSummarizeFetcher } from './summarize-api'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'

type SummaryItem = { text: string; length: number }


export default /*#__PURE__*/_defineComponent({
  __name: 'summarize-modal',
  props: {
    visible: { type: Boolean },
    text: { default: '' },
    targetLength: null,
    variationCount: { default: 3 },
    context: { default: '文章' }
  },
  emits: ["update:visible", "apply"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:visible', value: boolean): void
  (e: 'apply', text: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { visible: boolean, text: string, targetLength: number, variationCount: number, context: string };





const loading = ref(false)
const errorMessage = ref('')
const summaries = ref<SummaryItem[]>([])
const activeIndex = ref(0)
const abortController = ref<AbortController | null>(null)
const applied = ref(false)

const { user } = useAuth()
const { varuna } = useHostServices()
const vm = getCurrentInstance()!.proxy as unknown as {
  $axios: {
    post: (
      url: string,
      data: unknown,
      config?: { timeout?: number }
    ) => Promise<unknown>
    isCancel?: (err: unknown) => boolean
  }
}

const originalLength = computed(() => (props.text || '').length)
const currentSummary = computed(
  () => summaries.value[activeIndex.value] || { text: '', length: 0 }
)
const diffText = computed(() => {
  const d = currentSummary.value.length - originalLength.value
  if (d === 0) return '±0字'
  return (d > 0 ? '+' : '') + d + '字'
})

function onInput(value: boolean) {
  emit('update:visible', value)
}

function close() {
  cancelInflight()
  emit('update:visible', false)
}

function onOpened() {
  activeIndex.value = 0
  errorMessage.value = ''
  summaries.value = []
  applied.value = false
  fetchSummaries()
}

function onClosed() {
  cancelInflight()
  if (!applied.value && summaries.value.length > 0) {
    reportEvent('dismiss')
  }
}

function cancelInflight() {
  if (abortController.value) {
    abortController.value.abort()
    abortController.value = null
  }
  loading.value = false
}

function fetchSummaries() {
  cancelInflight()
  errorMessage.value = ''
  summaries.value = []

  if (!props.text || props.text.length < 50) {
    errorMessage.value = '文章が短いため、短縮提案の対象外です。'
    return
  }
  if (props.targetLength <= 0 || props.targetLength >= props.text.length) {
    errorMessage.value = '目標字数が不正です。'
    return
  }

  const fetcher = buildSummarizeFetcher(vm)
  if (!fetcher) {
    errorMessage.value =
      '短縮機能が利用できません。時間をおいて再度お試しください。'
    return
  }

  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null
  abortController.value = controller
  loading.value = true

  fetcher({
    text: props.text,
    targetLength: props.targetLength,
    variationCount: props.variationCount,
    context: props.context,
    signal: controller ? controller.signal : null
  })
    .then((next: SummaryItem[]) => {
      if (abortController.value !== controller) return
      abortController.value = null
      loading.value = false
      summaries.value = Array.isArray(next) ? next : []
      activeIndex.value = 0
    })
    .catch((err: { response?: { status?: number }; code?: string }) => {
      if (abortController.value !== controller) return
      abortController.value = null
      loading.value = false
      if (vm.$axios.isCancel?.(err)) return
      const status = err && err.response && err.response.status
      if (status === 401 || err.code === 'UNAUTHENTICATED') {
        errorMessage.value = 'ログインが必要です。'
      } else if (status === 403) {
        errorMessage.value = 'ヤギプライム会員限定機能です。'
      } else if (status === 429) {
        errorMessage.value =
          '本日の利用回数上限に達しました。明日またお試しください。'
      } else if (status === 502) {
        errorMessage.value =
          'AIサービスが一時的に利用できません。しばらくしてから再度お試しください。'
      } else {
        errorMessage.value =
          '短縮案の取得に失敗しました。しばらくしてから再度お試しください。'
      }
    })
}

function applyCurrent() {
  const picked = currentSummary.value
  if (!picked || !picked.text) return
  applied.value = true
  reportEvent('accept', { candidateIndex: activeIndex.value })
  emit('apply', picked.text)
  emit('update:visible', false)
}

function reportEvent(action: string, extra?: { candidateIndex?: number }) {
  const uid = user.value?.uid
  const endpoint = varuna.endPoints.proofreadEvent
  if (!uid || !endpoint) return
  const payload: Record<string, unknown> = {
    uuid: uid,
    feature: 'summarize',
    action
  }
  if (extra && typeof extra.candidateIndex === 'number') {
    payload.candidate_index = extra.candidateIndex
  }
  try {
    vm.$axios.post(endpoint, payload, { timeout: 5000 }).catch(() => {})
  } catch {
    /* noop */
  }
}

return { __sfc: true,props, emit, loading, errorMessage, summaries, activeIndex, abortController, applied, user, varuna, vm, originalLength, currentSummary, diffText, onInput, close, onOpened, onClosed, cancelInflight, fetchSummaries, applyCurrent, reportEvent }
}

})